import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Edit2, Check, X, FileText, Waves, Cloud, Home, Droplets, Wind, Download } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { Device, DeviceType } from '../types';
import deviceService from '../services/deviceService';
import LatestDataModal from '../components/LatestDataModal';
import ExportModal from '../components/ExportModal';
import {
  PageContainer,
  Header,
  Title,
  HeaderActions,
  ExportButton,
  SearchContainer,
  SearchInput,
  SearchIcon,
  TableContainer,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  StatusBadge,
  Footer,
  ShowingText,
  PaginationContainer,
  PaginationSelect,
  PaginationControls,
  PaginationButton,
  LoadingContainer,
  ErrorContainer,
  EmptyState,
  EditableNameCell,
  DeviceNameSpan,
  EditInput,
  EditButton,
  SaveButton,
  CancelButton,
  ViewDataIconButton,
  DeviceTypeBadge
} from '../styles/DeviceListStyles';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;


// Animated wrapper components
const AnimatedTableRow = styled(TableRow)<{ $index: number }>`
  animation: ${fadeIn} 0.5s ease-out;
  animation-delay: ${props => props.$index * 0.03}s;
  animation-fill-mode: both;
`;

const AnimatedEmptyState = styled(EmptyState)`
  animation: ${fadeIn} 0.6s ease-out;
`;

const AnimatedErrorContainer = styled(ErrorContainer)`
  animation: ${slideIn} 0.4s ease-out;
`;

// Helper function to get device type icon and color
const getDeviceTypeInfo = (deviceType?: DeviceType) => {
  switch (deviceType) {
    case 'netatmo_weather':
      return { icon: Home, color: '#22c55e', label: 'Weather Station' };
    case 'netatmo_outdoor':
      return { icon: Cloud, color: '#0ea5e9', label: 'Outdoor Module' };
    case 'netatmo_rain':
      return { icon: Droplets, color: '#3b82f6', label: 'Rain Gauge' };
    case 'netatmo_wind':
      return { icon: Wind, color: '#14b8a6', label: 'Wind Gauge' };
    case 'netatmo_indoor':
      return { icon: Home, color: '#6b7280', label: 'Indoor Module' };
    case 'buoy':
    default:
      return { icon: Waves, color: '#60a5fa', label: 'Marine Buoy' }; // Changed from #1d388d to lighter blue
  }
};

const DeviceListPage: React.FC = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDevices, setTotalDevices] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [isLatestDataModalOpen, setIsLatestDataModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [expandedStations, setExpandedStations] = useState<Set<string>>(new Set());
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const fetchDevices = useCallback(async () => {
    try {
      setError(null);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined
      };

      const response = await deviceService.getDevices(params);

      if (response && response.devices) {
        setDevices(response.devices);
        setTotalDevices(response.total || response.devices.length);
        setTotalPages(response.totalPages || Math.ceil((response.total || response.devices.length) / itemsPerPage));
      } else {
        throw new Error('Invalid response format from API');
      }

    } catch (err) {
      setError('Failed to fetch devices from server. Please check your connection and try again.');
      console.error('Error fetching devices:', err);
      setDevices([]);
      setTotalDevices(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRowClick = (device: Device, e: React.MouseEvent) => {
    // Don't navigate if clicking on edit controls
    if ((e.target as HTMLElement).closest('.edit-controls')) {
      return;
    }
    // Navigate to device details page for all devices
    navigate(`/devices/${device.id}`);
  };

  const handleNameClick = (device: Device, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/devices/${device.id}`);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleEditClick = (device: Device, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDeviceId(device.id);
    setEditingName(device.name || `Marine Buoy ${device.deviceId.slice(-4)}`);
  };

  const handleSaveClick = async (deviceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!editingName.trim()) {
      setError('Device name cannot be empty');
      return;
    }

    try {
      await deviceService.updateDevice(deviceId, editingName.trim());
      setEditingDeviceId(null);
      setEditingName('');
      await fetchDevices(); // Refresh the list
    } catch (err) {
      setError('Failed to update device name');
      console.error('Error updating device:', err);
    }
  };

  const handleCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDeviceId(null);
    setEditingName('');
  };

  const formatLastUpdate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${Math.floor(diffInMinutes)} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  const getStatusText = (status: 'online' | 'offline' | 'warning') => {
    switch (status) {
      case 'online': return 'Active';
      case 'warning': return 'Warning';
      case 'offline': return 'Inactive';
      default: return 'Unknown';
    }
  };

  const handleViewDataClick = (device: Device, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDevice(device);
    setIsLatestDataModalOpen(true);
  };

  const toggleStationExpansion = (stationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedStations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stationId)) {
        newSet.delete(stationId);
      } else {
        newSet.add(stationId);
      }
      return newSet;
    });
  };

  const renderDeviceRow = (device: Device, isModule: boolean = false) => {
    const typeInfo = getDeviceTypeInfo(device.deviceType);
    const TypeIcon = typeInfo.icon;

    return (
      <AnimatedTableRow
        key={device.id}
        onClick={(e) => handleRowClick(device, e)}
        $index={devices.indexOf(device)}
        style={isModule ? { backgroundColor: '#1a1a1a' } : {}}
      >
        <TableCell>
          <DeviceTypeBadge $color={typeInfo.color}>
            {isModule && <span style={{ marginRight: '8px', color: '#666' }}>└─</span>}
            <TypeIcon size={16} />
            <span>{typeInfo.label}</span>
          </DeviceTypeBadge>
        </TableCell>
        <TableCell>
          {editingDeviceId === device.id ? (
            <EditableNameCell className="edit-controls">
              <EditInput
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveClick(device.id, e as any);
                  } else if (e.key === 'Escape') {
                    handleCancelClick(e as any);
                  }
                }}
                autoFocus
              />
              <SaveButton onClick={(e) => handleSaveClick(device.id, e)}>
                <Check size={14} />
              </SaveButton>
              <CancelButton onClick={handleCancelClick}>
                <X size={14} />
              </CancelButton>
            </EditableNameCell>
          ) : (
            <EditableNameCell>
              <DeviceNameSpan onClick={(e) => handleNameClick(device, e)}>
                {device.name || `Marine Buoy ${device.deviceId.slice(-4)}`}
                {device.isStation && device.moduleCount && device.moduleCount > 0 && (
                  <span style={{ marginLeft: '8px', color: '#666', fontSize: '12px' }}>
                    ({device.moduleCount} module{device.moduleCount !== 1 ? 's' : ''})
                  </span>
                )}
              </DeviceNameSpan>
              <EditButton className="edit-controls" onClick={(e) => handleEditClick(device, e)}>
                <Edit2 size={14} />
              </EditButton>
            </EditableNameCell>
          )}
        </TableCell>
        <TableCell>
          {device.deviceId}
        </TableCell>
        <TableCell>
          <StatusBadge status={device.status}>
            {getStatusText(device.status)}
          </StatusBadge>
        </TableCell>
        <TableCell>
          {device.lastMessageAt ? formatLastUpdate(device.lastMessageAt) : 'Never'}
        </TableCell>
        <TableCell>
          <div style={{ display: 'flex', gap: '8px' }}>
            {device.isStation && device.modules && device.modules.length > 0 && (
              <button
                onClick={(e) => toggleStationExpansion(device.id, e)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  fontSize: '12px'
                }}
              >
                {expandedStations.has(device.id) ? '▼' : '▶'} Modules
              </button>
            )}
            <ViewDataIconButton onClick={(e) => handleViewDataClick(device, e)}>
              <FileText size={16} />
            </ViewDataIconButton>
          </div>
        </TableCell>
      </AnimatedTableRow>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          Loading devices...
        </LoadingContainer>
      </PageContainer>
    );
  }

  const startIndex = (currentPage - 1) * itemsPerPage + 1;

  return (
    <PageContainer>
      <Header>
        <Title>Devices</Title>
        <HeaderActions>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Search by name, device ID, or serial number..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <SearchIcon>
              <Search size={16} />
            </SearchIcon>
          </SearchContainer>
          <ExportButton onClick={() => setIsExportModalOpen(true)}>
            <Download size={16} />
            Export Metrics
          </ExportButton>
        </HeaderActions>
      </Header>

      {error && (
        <AnimatedErrorContainer>
          {error}
        </AnimatedErrorContainer>
      )}

      {devices.length === 0 && !loading ? (
        <AnimatedEmptyState>
          No devices found
        </AnimatedEmptyState>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Device Type</TableHeaderCell>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Device ID</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Last Update</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {devices.map((device) => {
                  const isExpanded = expandedStations.has(device.id);
                  const hasModules = device.modules && device.modules.length > 0;

                  return (
                    <React.Fragment key={device.id}>
                      {renderDeviceRow(device)}
                      {device.isStation && hasModules && isExpanded && device.modules!.map((module) => renderDeviceRow(module, true))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Footer>
            <ShowingText>
              Showing {totalDevices > 0 ? startIndex : 0} result{totalDevices === 1 ? '' : 's'}
              {totalDevices > 1 && ` of ${totalDevices}`}
            </ShowingText>

            <PaginationContainer>
              <span>Per page:</span>
              <PaginationSelect value={itemsPerPage} onChange={handleItemsPerPageChange}>
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </PaginationSelect>

              {totalPages > 1 && (
                <PaginationControls>
                  <PaginationButton
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    1
                  </PaginationButton>

                  {currentPage > 2 && <span>...</span>}

                  {currentPage > 1 && currentPage < totalPages && (
                    <PaginationButton active>
                      {currentPage}
                    </PaginationButton>
                  )}

                  {currentPage < totalPages - 1 && <span>...</span>}

                  {totalPages > 1 && (
                    <PaginationButton
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      active={currentPage === totalPages}
                    >
                      {totalPages}
                    </PaginationButton>
                  )}
                </PaginationControls>
              )}
            </PaginationContainer>
          </Footer>

          {selectedDevice && (
            <LatestDataModal
              isOpen={isLatestDataModalOpen}
              onClose={() => setIsLatestDataModalOpen(false)}
              device={selectedDevice}
            />
          )}

          <ExportModal
            isOpen={isExportModalOpen}
            onClose={() => setIsExportModalOpen(false)}
            devices={devices}
          />
        </>
      )}
    </PageContainer>
  );
};

export default DeviceListPage;
